// src/pages/personal/analytics/CustomerSection/components/NewClientsChart.tsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart } from '@/components/ui/Charts';
import { COLORS } from '@/theme';
// Импортируем форматтеры
import { formatNumber, formatSmartDate } from '@/utils/Formatters';
// Импортируем процессор данных для графиков
import { processChartData } from '../../utils/chartDataProcessor';
// Импортируем стили
import styles from '../CustomerSection.module.css';

interface NewClientsTrend {
  month: string; // '2023-01'
  month_name: string; // 'Jan 2023'
  count: number;
  formatted_month?: string; // Для предварительно форматированной даты
}

interface NewClientsChartProps {
  data: NewClientsTrend[];
}

const NewClientsChart: React.FC<NewClientsChartProps> = ({ data }) => {
  const { t, i18n } = useTranslation('customer');
  const currentLocale = i18n.language;
  
  // Обрабатываем данные с использованием общего процессора
  const processedData = useMemo(() => {
    // Преобразуем данные в формат, понятный для processChartData
    const formattedData = data.map(item => ({
      name: item.month_name || formatSmartDate(`${item.month}-01T00:00:00Z`, false, currentLocale),
      displayName: item.month_name || formatSmartDate(`${item.month}-01T00:00:00Z`, false, currentLocale),
      month: parseInt(item.month.split('-')[1]),
      year: parseInt(item.month.split('-')[0]),
      count: item.count
    }));
    
    // Используем тот же формат, что и для других графиков
    // @ts-ignore: Игнорируем ошибку типизации для MetricType
    return processChartData(formattedData, 'year', 'count', t);
  }, [data, t, currentLocale]);
  
  // Определяем серии данных
  const series = [
    {
      name: t('new_clients.count'),
      dataKey: 'count',
      color: COLORS.primary,
      strokeWidth: 2,
    }
  ];
  
  // Настройки для тултипа
  const tooltipFormatter = (value: number) => {
    return formatNumber(value, 0, true, currentLocale);
  };
  
  return (
    <div className={styles.newClientsChartContainer}>
      <LineChart
        data={processedData}
        series={series}
        xAxisKey="name"
        yAxisLabel={t('new_clients.count')}
        showGrid={true}
        showLegend={true}
        tooltipFormatter={tooltipFormatter}
        size="md"
        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
      />
    </div>
  );
};

export default NewClientsChart;